import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { SkeletonList } from "@/components/Skeletons";
import { EmptyState } from "@/components/EmptyState";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { formatDate, relativeDate } from "@/lib/format";
import client from "@/lib/api";

const STATUS = {
    en_cola: ["En cola", "border-line bg-background text-subtle"],
    registrado: ["Registrado", "border-line bg-background text-ink"],
    enviado: ["Enviado", "border-terracotta/30 bg-terracotta/10 text-terracotta"],
    fallido: ["Fallido", "border-destructive/30 bg-destructive/5 text-destructive"],
};
const KIND = {
    welcome: "Bienvenida", verify: "Verificación", reset: "Recuperar contraseña",
    routine: "Rutina lista", reminder: "Recordatorio",
};

export default function AdminEmails() {
    const [items, setItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [detail, setDetail] = useState(null);
    const pageSize = 15;

    const load = useCallback(async () => {
        setItems(null);
        const { data } = await client.get("/admin/emails", { params: { page, page_size: pageSize } });
        setItems(data.items);
        setTotal(data.total);
    }, [page]);

    useEffect(() => { load(); }, [load]);

    const open = async (id) => {
        const { data } = await client.get(`/admin/emails/${id}`);
        setDetail(data);
    };

    const pages = Math.ceil(total / pageSize) || 1;

    return (
        <>
            <PageHeader title="Correos enviados"
                subtitle="Registro de todo lo que sale (o saldría) desde MYAVEREN: a quién, cuándo y con qué estado." />

            <div className="mb-5 rounded-lg border border-line bg-background px-4 py-3 text-[13px] leading-[1.6] text-subtle">
                Mientras no haya una clave de envío configurada, los correos se guardan en modo
                <strong className="text-ink"> registrado</strong> y no salen al mundo real. Puedes ver aquí
                exactamente qué se habría enviado.
            </div>

            {items === null ? <SkeletonList rows={6} /> : items.length === 0 ? (
                <EmptyState testId="emails-empty" title="Todavía no hay correos"
                    text="En cuanto alguien se registre o genere su rutina, aquí aparecerá el registro del correo." />
            ) : (
                <div className="space-y-4">
                    <div className="overflow-hidden rounded-lg border border-line bg-card">
                        {items.map((m) => {
                            const [sl, sc] = STATUS[m.status] || STATUS.registrado;
                            return (
                                <button key={m.id} onClick={() => open(m.id)} data-testid={`email-row-${m.id}`}
                                    className="flex w-full items-center gap-4 border-b border-line px-4 py-3 text-left last:border-0 transition-colors duration-150 hover:bg-background/60">
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-[15px] font-medium text-ink">{m.subject}</p>
                                        <p className="truncate text-[13px] text-subtle">
                                            {KIND[m.kind] || m.kind} · para {m.to_email}
                                        </p>
                                    </div>
                                    <span className="hidden text-[12px] text-subtle sm:block">{relativeDate(m.created_at)}</span>
                                    <span className={`rounded-full border px-2.5 py-0.5 text-[12px] font-medium ${sc}`}>{sl}</span>
                                </button>
                            );
                        })}
                    </div>
                    {pages > 1 && (
                        <div className="flex items-center justify-between">
                            <span className="text-[13px] text-subtle">Página {page} de {pages}</span>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                                <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="max-h-[85vh] overflow-auto sm:max-w-lg">
                    {detail && (
                        <>
                            <DialogHeader>
                                <DialogTitle>{detail.subject}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3 text-[14px]">
                                <p className="text-subtle">Para: <span className="text-ink">{detail.to_name} ({detail.to_email})</span></p>
                                <p className="text-subtle">Fecha: <span className="text-ink">{formatDate(detail.created_at)}</span></p>
                                <p className="text-subtle">Estado: <span className="text-ink">{(STATUS[detail.status] || [])[0]}</span> · Intentos: {detail.attempts}</p>
                                {detail.error && <p className="text-destructive">Error: {detail.error}</p>}
                                <div className="rounded-lg border border-line bg-background p-4">
                                    <p className="mb-2 text-[12px] font-medium uppercase tracking-wide text-subtle">Contenido</p>
                                    <pre className="whitespace-pre-wrap font-sans text-[14px] leading-[1.6] text-ink">{detail.text}</pre>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
